import { useCallback, useEffect, useRef, useState } from "react";

import { Expand } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getErrorMessage } from "@/lib/api";
import { getModuleReaderMetadata, getModuleReaderPageBlob } from "@/lib/moduleReaderApi";
import { ModuleReaderPageImage } from "@/components/modules/ModuleReaderPageImage";
import { ModuleReaderState } from "@/components/modules/ModuleReaderState";
import { ModuleReaderToolbar } from "@/components/modules/ModuleReaderToolbar";


const clearReaderImage = (setPageImageUrl) => {
  setPageImageUrl((existingImageUrl) => {
    if (existingImageUrl) {
      URL.revokeObjectURL(existingImageUrl);
    }
    return "";
  });
};


const replaceReaderImage = (setPageImageUrl, nextImageUrl) => {
  setPageImageUrl((existingImageUrl) => {
    if (existingImageUrl) {
      URL.revokeObjectURL(existingImageUrl);
    }
    return nextImageUrl;
  });
};


const getMetadataErrorVariant = (error) => (error?.response?.status === 403 ? "access_required" : error?.response?.status === 404 ? "not_available" : "error");


const getPageErrorVariant = (error) => (error?.response?.status === 403 ? "access_removed" : error?.response?.status === 404 ? "content_not_available" : "error");

const DEFAULT_ZOOM_LEVEL = 100;
const MIN_ZOOM_LEVEL = 50;
const MAX_ZOOM_LEVEL = 150;
const ZOOM_STEP = 10;


export const ProtectedModuleReader = ({ moduleCode }) => {
  const [metadata, setMetadata] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");
  const [pageInputError, setPageInputError] = useState("");
  const [pageImageUrl, setPageImageUrl] = useState("");
  const [pageLoading, setPageLoading] = useState(false);
  const [state, setState] = useState({ variant: "loading", message: "" });
  const [focusMode, setFocusMode] = useState(false);
  const metadataRequestRef = useRef(0);
  const focusModeSnapshotRef = useRef({ zoomLevel: DEFAULT_ZOOM_LEVEL, zoomMode: "manual" });
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM_LEVEL);
  const [zoomMode, setZoomMode] = useState("manual");
  const readerAvailable = Boolean(metadata?.reader_available);
  const readerReady = state.variant === "ready";
  const pageCount = metadata?.page_count || 0;
  const zoomLabel = zoomMode === "fit" ? "Fit width" : `Zoom ${zoomLevel}%`;

  useEffect(() => {
    return () => {
      clearReaderImage(setPageImageUrl);
    };
  }, []);

  useEffect(() => {
    if (!focusMode) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setFocusMode(false);
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [focusMode]);

  const openFocusMode = useCallback(() => {
    focusModeSnapshotRef.current = { zoomLevel, zoomMode };
    setZoomMode("fit");
    setZoomLevel(DEFAULT_ZOOM_LEVEL);
    setFocusMode(true);
  }, [zoomLevel, zoomMode]);

  const exitFocusMode = useCallback(() => {
    const snapshot = focusModeSnapshotRef.current;
    setFocusMode(false);
    setZoomLevel(snapshot.zoomLevel);
    setZoomMode(snapshot.zoomMode);
  }, []);

  useEffect(() => {
    const requestId = metadataRequestRef.current + 1;
    metadataRequestRef.current = requestId;

    const loadMetadata = async () => {
      setMetadata(null);
      setCurrentPage(1);
      setPageInput("1");
      setPageInputError("");
      setZoomLevel(DEFAULT_ZOOM_LEVEL);
      setZoomMode("manual");
      setState({ variant: "loading", message: "" });
      clearReaderImage(setPageImageUrl);
      try {
        const response = await getModuleReaderMetadata(moduleCode);
        if (metadataRequestRef.current !== requestId) return;
        setMetadata(response);
        setCurrentPage(response.initial_page || 1);
        setState({
          variant: response.reader_available ? "ready" : "content_not_available",
          message: response.message || "",
        });
      } catch (error) {
        if (metadataRequestRef.current !== requestId) return;
        const nextVariant = getMetadataErrorVariant(error);
        setState({ variant: nextVariant, message: getErrorMessage(error) });
      }
    };

    void loadMetadata();
    return () => {
      metadataRequestRef.current += 1;
    };
  }, [moduleCode]);

  const loadPage = useCallback(async (nextPage) => {
    if (!readerAvailable) return;
    setPageLoading(true);
    try {
      const pageBlob = await getModuleReaderPageBlob(moduleCode, nextPage);
      const nextImageUrl = URL.createObjectURL(pageBlob);
      replaceReaderImage(setPageImageUrl, nextImageUrl);
      setCurrentPage(nextPage);
      setState({ variant: "ready", message: "" });
    } catch (error) {
      clearReaderImage(setPageImageUrl);
      const nextVariant = getPageErrorVariant(error);
      const fallback = nextVariant === "access_removed" ? "Access to this module has been removed." : "We could not load this reader page.";
      setState({ variant: nextVariant, message: getErrorMessage(error, fallback) });
    } finally {
      setPageLoading(false);
    }
  }, [moduleCode, readerAvailable]);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const handleGoToPage = useCallback(() => {
    if (!pageCount) return;
    const parsedPage = Number.parseInt(pageInput, 10);
    if (Number.isNaN(parsedPage) || parsedPage < 1 || parsedPage > pageCount) {
      setPageInputError(`Enter a page between 1 and ${pageCount}.`);
      return;
    }
    setPageInputError("");
    setCurrentPage(parsedPage);
  }, [pageCount, pageInput]);

  const handlePageInputChange = useCallback((value) => {
    const digitsOnly = value.replace(/[^0-9]/g, "");
    setPageInput(digitsOnly);
    setPageInputError("");
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoomMode("manual");
    setZoomLevel((currentZoom) => Math.min(MAX_ZOOM_LEVEL, (zoomMode === "fit" ? DEFAULT_ZOOM_LEVEL : currentZoom) + ZOOM_STEP));
  }, [zoomMode]);

  const handleZoomOut = useCallback(() => {
    setZoomMode("manual");
    setZoomLevel((currentZoom) => Math.max(MIN_ZOOM_LEVEL, (zoomMode === "fit" ? DEFAULT_ZOOM_LEVEL : currentZoom) - ZOOM_STEP));
  }, [zoomMode]);

  const handleResetZoom = useCallback(() => {
    setZoomMode("fit");
    setZoomLevel(DEFAULT_ZOOM_LEVEL);
  }, []);

  useEffect(() => {
    if (!readerAvailable || !readerReady) return;
    void loadPage(currentPage);
  }, [currentPage, loadPage, readerAvailable, readerReady]);

  if (!readerReady) {
    return <ModuleReaderState variant={state.variant} message={state.message} />;
  }

  const readerContent = (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="module-reader-focus-toggle-bar">
        <h2 className="text-[1.65rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="module-reader-title">
          {metadata?.title || moduleCode}
        </h2>
        <Button type="button" variant="outline" className="rounded-full border-[#f1b963] bg-white/90 text-slate-900 hover:bg-[#fff3de] hover:text-slate-950" onClick={openFocusMode} data-testid="module-reader-focus-mode-button">
          <Expand className="h-4 w-4" />
          Reader mode
        </Button>
      </div>
      <ModuleReaderToolbar
        pageCount={pageCount}
        pageInput={pageInput}
        onPageInputChange={handlePageInputChange}
        onPageInputSubmit={handleGoToPage}
        pageInputError={pageInputError}
        onPrevious={() => setCurrentPage((current) => Math.max(1, current - 1))}
        onNext={() => setCurrentPage((current) => Math.min(pageCount || current, current + 1))}
        previousDisabled={pageLoading || currentPage <= 1}
        nextDisabled={pageLoading || currentPage >= pageCount}
        pageLoading={pageLoading}
        zoomLabel={zoomLabel}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetZoom={handleResetZoom}
        canZoomIn={zoomMode === "fit" || zoomLevel < MAX_ZOOM_LEVEL}
        canZoomOut={zoomMode === "fit" || zoomLevel > MIN_ZOOM_LEVEL}
      />
      <ModuleReaderPageImage
        imageUrl={pageImageUrl}
        moduleTitle={metadata?.title || moduleCode}
        currentPage={currentPage}
        loading={pageLoading || !pageImageUrl}
        zoomLevel={zoomLevel}
        zoomMode={zoomMode}
        focusMode={focusMode}
        onPrevious={() => setCurrentPage((current) => Math.max(1, current - 1))}
        onNext={() => setCurrentPage((current) => Math.min(pageCount || current, current + 1))}
        previousDisabled={pageLoading || currentPage <= 1}
        nextDisabled={pageLoading || currentPage >= pageCount}
        pageCount={pageCount}
        onExitFocusMode={exitFocusMode}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        canZoomIn={zoomMode === "fit" || zoomLevel < MAX_ZOOM_LEVEL}
        canZoomOut={zoomMode === "fit" || zoomLevel > MIN_ZOOM_LEVEL}
      />
    </>
  );

  return (
    <div className={focusMode ? "fixed inset-0 z-50 bg-[#f5f2e9]" : ""} data-testid={focusMode ? "module-reader-focus-mode-shell" : "module-reader-shell"}>
      <Card className={focusMode ? "flex h-full flex-col rounded-none border-none bg-[#f5f2e9] shadow-none" : "border-stone-200/80 bg-white/92 shadow-none"} data-testid="module-reader-card">
        {!focusMode ? (
          <CardContent className="space-y-4 px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
            {readerContent}
          </CardContent>
        ) : (
          <CardContent className="flex h-full flex-1 overflow-hidden p-0">
            <ModuleReaderPageImage
              imageUrl={pageImageUrl}
              moduleTitle={metadata?.title || moduleCode}
              currentPage={currentPage}
              loading={pageLoading || !pageImageUrl}
              zoomLevel={zoomLevel}
              zoomMode={zoomMode}
              focusMode={focusMode}
              onPrevious={() => setCurrentPage((current) => Math.max(1, current - 1))}
              onNext={() => setCurrentPage((current) => Math.min(pageCount || current, current + 1))}
              previousDisabled={pageLoading || currentPage <= 1}
              nextDisabled={pageLoading || currentPage >= pageCount}
              pageCount={pageCount}
              onExitFocusMode={exitFocusMode}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              canZoomIn={zoomMode === "fit" || zoomLevel < MAX_ZOOM_LEVEL}
              canZoomOut={zoomMode === "fit" || zoomLevel > MIN_ZOOM_LEVEL}
            />
          </CardContent>
        )}
      </Card>
    </div>
  );
};
